import React, { useEffect, useMemo, useState } from "react";
// MB-1366: ref<->source converters live in a shared util so the manual
// Create Slideshow dialog seeds from the same saved preferences.
import {
    colorRefToSource,
    colorSourceToRef,
} from "../../../utils/autoVideoColorRef";
import axios from "axios";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Button } from "../../misc/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../misc/Card";
import CustomToast from "../../misc/CustomToast";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useAppSelector from "../../../hooks/useAppSelector";
import { getAuthHeader } from "../../../utils/authHeader";
import { cn } from "../../../utils/classMerger";
import LoadingScreen from "../../../screens/LoadingScreen";
import SlideshowLogoSelector from "../../listings/slideshow/SlideshowLogoSelector";
import CorporateHeadshotField from "../../listings/slideshow/CorporateHeadshotField";
import BrandColorSelector from "../../misc/custom-posts/BrandColorSelector";
import FontSelector from "../../listings/slideshow/FontSelector";
import SlideshowTemplateCard from "../../listings/slideshow/SlideshowTemplateCard";
import { SLIDESHOW_TEMPLATES } from "../../listings/slideshow/slideshowTemplates";
import AutoVideoMusicSelector from "./AutoVideoMusicSelector";
import { Switch } from "../../misc/Switch";
import {
    ColorSource,
    DEFAULT_BRAND_COLORS,
} from "../../misc/custom-posts/CaptionedVideoMediaStep";
import {
    AutoVideoLogoRef,
    BrandingSettingsResponse,
} from "../../../types/slideshow";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface AutoVideoPreferencesProps {
    // Render just the form (no page chrome / back button) for use inside a dialog.
    embedded?: boolean;
    // Called after a successful save with the value the user saved — e.g. to
    // refresh campaign-creation readiness and mirror the switch back to the host.
    onSaved?: (autoGenerateSlideshow: boolean) => void;
    // Embedded in the campaign flow: seed the auto-generate switch from the
    // per-campaign toggle (the value the campaign is submitted with) rather than
    // the saved global default, so the switch matches what the user just set.
    autoGenerateValue?: boolean;
    // The host renders its own commit control (the MLS wizard's Save & Finish)
    // and reports the outcome, so this form stays silent on success.
    hideSaveButton?: boolean;
    // Filled with this form's save handler so a host footer can commit it. The
    // handler refuses until the branding fetch has actually succeeded — saving
    // before then would write the empty defaults over the stored values.
    saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
    // Whether the form has loaded and is safe to save, so a host can disable
    // its own commit control rather than let it fail.
    onReadyChange?: (ready: boolean) => void;
}

const AutoVideoPreferences: React.FC<AutoVideoPreferencesProps> = ({
    embedded = false,
    onSaved,
    autoGenerateValue,
    hideSaveButton = false,
    saveRef,
    onReadyChange,
}) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);
    const corporateHeadshot = useAppSelector(
        (state) => state.auth.user?.corporateHeadshot
    );

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // A failed fetch leaves every field at its component default, so saving
    // then would overwrite the stored settings with blanks. `loading` alone
    // doesn't cover it — the finally clears that on the error path too.
    const [loadFailed, setLoadFailed] = useState(false);

    const [logoRef, setLogoRef] = useState<AutoVideoLogoRef | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    // A caller that supplies autoGenerateValue seeds the switch itself (the
    // campaign flow, from its per-campaign toggle). Everyone else must mirror
    // the saved global default — see the load effect below.
    const seedsOwnAutoGenerate = autoGenerateValue !== undefined;

    const [autoGenerate, setAutoGenerate] = useState(!!autoGenerateValue);

    const [colorSource, setColorSource] = useState<ColorSource | undefined>();

    const [font, setFont] = useState<string>("Inter");

    const [preferredTemplates, setPreferredTemplates] = useState<string[]>([]);

    const [musicTracks, setMusicTracks] = useState<string[]>([]);

    const templateOptions = useMemo(
        () => SLIDESHOW_TEMPLATES.filter((t) => !t.hidden),
        []
    );

    useEffect(() => {
        const loadPreferences = async (): Promise<void> => {
            try {
                setLoading(true);
                setLoadFailed(false);
                const headers = await getAuthHeader();
                const { data } = await axios.get<BrandingSettingsResponse>(
                    `${backendUrl}/settings/branding`,
                    { headers }
                );
                setLogoRef(data.autoVideoLogo ?? null);
                setColorSource(
                    colorRefToSource(data.autoVideoColor) ??
                        (data.brokerageId
                            ? { type: "brokerage", id: data.brokerageId }
                            : { type: "custom", colors: DEFAULT_BRAND_COLORS })
                );
                // Skipping this for a caller that does NOT seed its own switch
                // renders it permanently off, and saving then writes that off
                // back over the user's real setting.
                if (!seedsOwnAutoGenerate)
                    setAutoGenerate(data.autoGenerateSlideshow ?? false);
                setFont(data.autoVideoFont ?? "Inter");
                setPreferredTemplates(data.autoVideoPreferredTemplates ?? []);
                setMusicTracks(data.autoVideoMusic ?? []);
            } catch {
                setLoadFailed(true);
                toast.error(
                    <CustomToast
                        message="Failed to load auto-video preferences"
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                setLoading(false);
            }
        };
        loadPreferences();
    }, [seedsOwnAutoGenerate]);

    // Safe to save only once the stored values are actually in hand.
    const ready = !loading && !loadFailed;

    useEffect(() => {
        onReadyChange?.(ready);
        return () => onReadyChange?.(false);
    }, [ready, onReadyChange]);

    // When the user picks a brokerage logo, default the colors to that same
    // brokerage. Guarded on an actual brokerage change so the selector's
    // mount echo can't clobber a separately-saved color choice on load.
    const handleLogoRefChange = (ref: AutoVideoLogoRef | null): void => {
        const prevBrokerageId =
            logoRef?.source === "brokerage" ? logoRef.brokerageId : undefined;
        if (
            ref?.source === "brokerage" &&
            ref.brokerageId &&
            ref.brokerageId !== prevBrokerageId
        ) {
            setColorSource({ type: "brokerage", id: ref.brokerageId });
        }
        setLogoRef(ref);
    };

    const toggleTemplate = (id: string): void => {
        setPreferredTemplates((prev) =>
            prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
        );
    };

    const handleSave = async (): Promise<boolean> => {
        // Refusing here — not just hiding the button — is what stops a host
        // footer committing component defaults over the stored settings.
        if (!ready) {
            toast.error(
                <CustomToast
                    message="Your video settings haven't loaded yet. Please try again in a moment."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return false;
        }
        if (autoGenerate) {
            let missing = "";
            if (!logoRef) missing = "a logo";
            else if (!colorSource) missing = "a brand color";
            else if (!corporateHeadshot) missing = "a corporate headshot";
            if (missing) {
                toast.error(
                    <CustomToast
                        message={`Please add ${missing} — logo, brand color, and corporate headshot are all required for auto-generated videos.`}
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                return false;
            }
        }
        try {
            setSaving(true);
            const headers = await getAuthHeader();
            const payload: Partial<BrandingSettingsResponse> = {
                autoGenerateSlideshow: autoGenerate,
                autoVideoFont: font,
                autoVideoPreferredTemplates: preferredTemplates,
                autoVideoMusic: musicTracks,
                // Mark setup complete so the campaign-creation toggle unlocks
                // (gated alongside logo / color / headshot presence). MB-1314.
                autoVideoSetupCompleted: true,
            };
            if (logoRef) payload.autoVideoLogo = logoRef;
            if (colorSource)
                payload.autoVideoColor = colorSourceToRef(colorSource);

            await axios.patch(`${backendUrl}/settings/branding`, payload, {
                headers,
            });
            // A host that renders its own commit control also owns the success
            // message — otherwise finishing the MLS wizard stacked this on top
            // of its own summary.
            if (!hideSaveButton) {
                toast.success(
                    <CustomToast
                        message="Auto-video preferences saved"
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            }
            onSaved?.(autoGenerate);
            return true;
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to save auto-video preferences"
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return false;
        } finally {
            setSaving(false);
        }
    };

    // No dependency array on purpose: the host must always hold the current
    // closure, and handleSave closes over every field on the form. Armed
    // unconditionally — handleSave itself refuses while unready, which is
    // safer than a null ref the host would silently skip over.
    useEffect(() => {
        if (!saveRef) return undefined;
        saveRef.current = handleSave;
        return () => {
            saveRef.current = null;
        };
    });

    return (
        <div
            className={
                embedded
                    ? ""
                    : cn("flex justify-center", isMobile ? "h-full" : "mt-4")
            }
        >
            <Card
                className={cn(
                    "w-full mx-auto",
                    embedded
                        ? "border-0 shadow-none mt-0"
                        : isMobile
                          ? "h-full m-0 rounded-none max-w-none"
                          : "h-auto max-w-4xl"
                )}
            >
                {!embedded && (
                    <div className="p-4">
                        <Button
                            id="back-to-settings-btn"
                            onClick={() => navigate("/settings")}
                            className="flex items-center"
                        >
                            <ArrowLeft className="mr-2 h-5 w-5" /> Back to
                            Settings
                        </Button>
                    </div>
                )}
                {!embedded && (
                    <CardHeader>
                        <CardTitle className="text-left">
                            Listing Video Settings
                        </CardTitle>
                        <p className="text-sm text-neutral-500">
                            Defaults used when Marabot automatically creates a
                            slideshow video for your listings at campaign launch
                            and renewal.
                        </p>
                    </CardHeader>
                )}
                <CardContent
                    className={cn("space-y-8", embedded && "p-0 space-y-6")}
                >
                    {loading ? (
                        <LoadingScreen />
                    ) : (
                        <>
                            <CorporateHeadshotField />

                            <hr className="border-gray-200" />

                            <div className="flex items-start gap-3">
                                <Switch
                                    id="auto-generate-slideshow"
                                    checked={autoGenerate}
                                    onCheckedChange={setAutoGenerate}
                                />
                                <div>
                                    <h4 className="text-base font-semibold text-primary">
                                        Automatically generate listing videos
                                    </h4>
                                    <p className="text-sm text-neutral-500">
                                        Create a branded video for new listings
                                        and at each campaign renewal, using the
                                        preferences below.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <h4 className="text-base font-semibold text-primary">
                                        Preferred Templates
                                    </h4>
                                    <p className="text-sm text-neutral-500">
                                        Auto-generated videos rotate through the
                                        templates you select. Leave all unselected and Marabot
                                        rotates through all of them.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 min-[640px]:grid-cols-2 sxl:grid-cols-3 gap-4">
                                    {templateOptions.map((template) => (
                                        <SlideshowTemplateCard
                                            key={template.id}
                                            template={template}
                                            selected={preferredTemplates.includes(
                                                template.id
                                            )}
                                            onSelect={() =>
                                                toggleTemplate(template.id)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>

                            <SlideshowLogoSelector
                                selectedLogo={logoUrl}
                                setSelectedLogo={setLogoUrl}
                                selectedReference={logoRef}
                                onReferenceChange={handleLogoRefChange}
                                onBrokerageChange={(id) =>
                                    setColorSource({ type: "brokerage", id })
                                }
                                required={autoGenerate}
                            />

                            <BrandColorSelector
                                value={colorSource}
                                onChange={setColorSource}
                            />

                            <FontSelector
                                selectedFont={font}
                                setSelectedFont={setFont}
                            />

                            <AutoVideoMusicSelector
                                selected={musicTracks}
                                onChange={setMusicTracks}
                            />

                            {!hideSaveButton && (
                                <div className="flex justify-end">
                                    <Button
                                        id="save-auto-video-preferences-btn"
                                        onClick={handleSave}
                                        disabled={saving || !ready}
                                        className={
                                            isMobile ? "w-full" : "w-auto"
                                        }
                                    >
                                        {saving ? (
                                            <>
                                                <LoaderCircle className="animate-spin h-5 w-5 mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save"
                                        )}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AutoVideoPreferences;
